import { useCallback, useRef } from 'react';
import { uploadFile } from '@/api/uploads.api';
import { useAuthStore } from '@/store/auth.store';
import { FileUploadError } from '@/utils/errors';
import { resolvePcmpUploadMimeType } from '../utils/pcmpUploadFile';

/**
 * Uploads a PCMP_DISBURSEMENT file (a deposit slip) and returns its key. A
 * file already uploaded is not sent again, so a retry after the API refused
 * the action only re-runs the action.
 */
export function usePcmpFileUploader() {
  const userId = useAuthStore((state) => state.user?.id);
  const uploaded = useRef(new Map<File, string>());

  return useCallback(
    async (file: File): Promise<string> => {
      if (userId === undefined) throw new Error('You are not signed in.');
      const known = uploaded.current.get(file);
      if (known) return known;
      const mimeType = resolvePcmpUploadMimeType(file);
      // The form schema already refused it; this narrows the type.
      if (!mimeType) throw new FileUploadError('The file must be a JPEG, PNG or PDF.');
      const key = await uploadFile({ file, mimeType, resourceType: 'PCMP_DISBURSEMENT', userId });
      uploaded.current.set(file, key);
      return key;
    },
    [userId],
  );
}
